import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-preact';
import { Localizer, Text } from 'preact-i18n';

const MAX_FEEDBACK_LENGTH = 5000;
const RATINGS = [1, 2, 3, 4, 5];

@observer
class JournalFeedback extends Component {
    constructor(props) {
        super(props);
        this.state = {
            feedback: '',
            rating: 0,
            submitting: false,
            submittedReviews: [],
            error: null,
        };
    }

    setRating = rating => {
        this.setState({ rating, error: null });
    }

    updateFeedback = e => {
        this.setState({ feedback: e.target.value, error: null });
    }

    submitFeedback = async e => {
        e.preventDefault();
        const { journal = {}, stores } = this.props;
        const { feedback, rating } = this.state;
        if (!journal.id || !journal.veterinary || !feedback.trim() || rating < 1 || rating > 5) {
            this.setState({ error: 'validation' });
            return;
        }

        this.setState({ submitting: true, error: null });
        try {
            const response = await stores.journalStore.submitFeedback(journal.id, feedback.trim(), rating);
            if (response?.status === 201) {
                this.setState(previousState => ({
                    feedback: '',
                    rating: 0,
                    submitting: false,
                    submittedReviews: response.data?.review
                        ? [...previousState.submittedReviews, response.data.review]
                        : previousState.submittedReviews,
                }));
                return;
            }
            this.setState({ submitting: false, error: response?.message || 'request' });
        } catch {
            this.setState({ submitting: false, error: 'request' });
        }
    }

    renderStars = (rating, interactive = false) => RATINGS.map(value => (
        interactive ? (
            <button
                key={value}
                type='button'
                class='btn btn-link border-0 p-1'
                role='radio'
                aria-label={`${value} / 5`}
                aria-checked={value === rating}
                onClick={() => this.setRating(value)}
            >
                <i class={`${value <= rating ? 'fa-solid' : 'fa-regular'} fa-star fs-4 text-warning`} />
            </button>
        ) : (
            <i key={value} class={`${value <= rating ? 'fa-solid' : 'fa-regular'} fa-star text-warning me-1`} />
        )
    ))

    render() {
        const { journal = {}, stores } = this.props;
        const {
            feedback, rating, submitting, submittedReviews, error,
        } = this.state;
        const veterinaries = stores.journalStore.veterinaries || [];
        const responsibleVeterinarian = veterinaries.find(veterinary => veterinary.id === journal.veterinary) || {};
        const veterinarianName = [responsibleVeterinarian.firstname, responsibleVeterinarian.lastname]
            .filter(Boolean)
            .join(' ')
            || journal.veterinaryName
            || (journal.veterinary ? `#${journal.veterinary}` : '');
        const existingReviews = Array.isArray(journal.adminReviews) ? journal.adminReviews : [];
        const reviews = [...existingReviews, ...submittedReviews];
        const canSubmit = !!journal.veterinary && !submitting;

        return (
            <section class='mx-2 mt-3 mb-4 p-3 rounded border border-info bg-body-tertiary'>
                <div class='fw-semibold mb-1'>
                    <i class='fa-duotone fa-message-pen me-2 text-info' />
                    <Text id='dyrejournal.journal.feedback-title'>Feedback to responsible veterinarian</Text>
                    {veterinarianName && <>: {veterinarianName}</>}
                </div>
                <p class='small text-body-secondary mb-3'>
                    <Text id='dyrejournal.journal.feedback-help'>
                        The written feedback is sent as an inbox message. The rating is stored for internal admin
                        review and is not sent to the veterinarian.
                    </Text>
                </p>

                {!journal.veterinary && (
                    <div class='alert alert-warning py-2'>
                        <Text id='dyrejournal.journal.feedback-no-veterinarian'>
                            Select and save a responsible veterinarian before sending feedback.
                        </Text>
                    </div>
                )}

                <form onSubmit={this.submitFeedback}>
                    <div class='mb-2'>
                        <label class='form-label fw-semibold mb-0'>
                            <Text id='dyrejournal.journal.feedback-rating'>Internal rating</Text>
                        </label>
                        <div role='radiogroup' aria-label='Internal rating'>
                            {this.renderStars(rating, true)}
                        </div>
                    </div>
                    <div class='mb-2'>
                        <label class='form-label fw-semibold' for={`journal-feedback-${journal.id}`}>
                            <Text id='dyrejournal.journal.feedback-message'>Inbox message</Text>
                        </label>
                        <Localizer>
                            <textarea
                                id={`journal-feedback-${journal.id}`}
                                class='form-control'
                                rows='4'
                                maxLength={MAX_FEEDBACK_LENGTH}
                                value={feedback}
                                onInput={this.updateFeedback}
                                disabled={!journal.veterinary || submitting}
                                placeholder={(
                                    <Text id='dyrejournal.journal.feedback-placeholder'>
                                        Write constructive feedback about this journal
                                    </Text>
                                )}
                            />
                        </Localizer>
                        <small class='text-body-secondary'>{feedback.length} / {MAX_FEEDBACK_LENGTH}</small>
                    </div>

                    {error && (
                        <div class='alert alert-danger py-2'>
                            {error === 'validation'
                                ? (
                                    <Text id='dyrejournal.journal.feedback-error'>
                                        Choose a rating and write feedback before sending.
                                    </Text>
                                )
                                : (error === 'request'
                                    ? (
                                        <Text id='dyrejournal.journal.feedback-request-error'>
                                            Feedback could not be sent. Try again.
                                        </Text>
                                    )
                                    : error)}
                        </div>
                    )}
                    {submittedReviews.length > 0 && (
                        <div class='alert alert-success py-2'>
                            <i class='fa-solid fa-check me-2' />
                            <Text id='dyrejournal.journal.feedback-sent'>
                                Feedback was sent and the internal rating was saved.
                            </Text>
                        </div>
                    )}

                    <button
                        type='submit'
                        class='btn btn-info rounded-pill'
                        disabled={!canSubmit || !feedback.trim() || rating < 1}
                    >
                        {submitting ? (
                            <>
                                <i class='fa-regular fa-spinner-third fa-spin me-2' />
                                <Text id='dyrejournal.journal.feedback-sending'>Sending...</Text>
                            </>
                        ) : (
                            <>
                                <i class='fa-duotone fa-paper-plane me-2' />
                                <Text id='dyrejournal.journal.feedback-send'>Send feedback</Text>
                            </>
                        )}
                    </button>
                </form>

                {reviews.length > 0 && (
                    <div class='mt-3 pt-3 border-top'>
                        <div class='small fw-semibold mb-2'>
                            <Text id='dyrejournal.journal.feedback-history'>Internal rating history</Text>
                        </div>
                        {reviews.map(review => (
                            <div class='small d-flex flex-wrap align-items-center gap-2 mb-1' key={review.uuidv4}>
                                <span>{this.renderStars(review.rating)}</span>
                                <span class='text-body-secondary'>
                                    {review.reviewerName || ''}
                                    {review.date && <> · {new Date(review.date).toLocaleDateString()}</>}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        );
    }
}

export default JournalFeedback;
